import { useMemo } from 'react'
import { useAppSelector } from '@store/hooks'
import { useGetDraftsQuery } from '@store/api/sessionApi'
import { useOfflineCollection } from '@hooks/useOfflineCollection'
import {
  pickPrimaryActiveSession,
  pickPrimaryResumableDraft,
} from '@utils/smokingSessionState'

/**
 * Resolves the user's current live session and resumable draft from API (online) or cache (offline).
 */
export function useActiveSmokingSession() {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const { isOnline } = useAppSelector((state) => state.offline)
  const { offlineSessions } = useOfflineCollection()

  const { data: draftsData, isLoading: draftsLoading } = useGetDraftsQuery(undefined, {
    skip: !isAuthenticated || !isOnline,
  })

  const draftPool = useMemo(() => {
    if (!isAuthenticated) return []
    if (isOnline) return draftsData?.drafts ?? []
    return offlineSessions.filter((s) => s.isDraft)
  }, [isAuthenticated, isOnline, draftsData?.drafts, offlineSessions])

  const activeSession = useMemo(() => pickPrimaryActiveSession(draftPool), [draftPool])
  const resumableDraft = useMemo(() => {
    if (activeSession) return null
    return pickPrimaryResumableDraft(draftPool)
  }, [draftPool, activeSession])

  return {
    activeSession,
    resumableDraft,
    isLoading: isOnline && draftsLoading,
  }
}
